import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import ClientLayout from "../components/layout/ClientLayout";
import axiosInstance from "../../../utils/axiosInstance";
import { getMediaUrl } from "../../../utils/mediaUrl";

const TYPE_BANK = 1;
const TYPE_USDT = 2;
const TYPE_JAZZ = 4;
const TYPE_EASY = 5;

const PKR_AMOUNTS  = [500, 1000, 5000, 10000, 20000, 50000];
const USDT_AMOUNTS = [10, 20, 100, 500, 1000, 5000];
const USDT_RATE    = 90;

// ─── Copy button ─────────────────────────────────────────────────────────────
function CopyBtn({ text }) {
  const [ok, setOk] = useState(false);
  return (
    <button
      onClick={() => {
        navigator.clipboard.writeText(text).then(() => {
          setOk(true);
          setTimeout(() => setOk(false), 1500);
        });
      }}
      style={{
        background: "none", border: "none", cursor: "pointer",
        color: ok ? "#02b181" : "#e8951a", fontSize: 18, padding: "0 6px",
        lineHeight: 1, flexShrink: 0,
      }}
      title="Copy"
    >
      {ok ? (
        <span style={{ color: "#02b181", fontSize: 16 }}>✓</span>
      ) : (
        <img
          src="/assets/autoWallet/png/copy-icon.png"
          alt="Copy"
          style={{ height: 20, width: "auto", display: "block" }}
        />
      )}
    </button>
  );
}

// ─── Read-only info row (account details) ────────────────────────────────────
function InfoRow({ label, value, placeholder = "", last = false }) {
  const displayValue = value != null ? String(value) : "";
  return (
    <div className="dw-field-row" style={{ marginBottom: last ? 40 : 0 }}>
      <span className="dw-field-label">{label}</span>
      <span className="dw-field-sep">|</span>
      <input
        type="text"
        readOnly
        value={displayValue}
        placeholder={placeholder}
        className={`dw-input dw-field-readonly${displayValue ? " has-value" : ""}`}
      />
      {displayValue ? <CopyBtn text={displayValue} /> : null}
    </div>
  );
}

// ─── Editable input row ───────────────────────────────────────────────────────
function InputRow({ icon, label, children }) {
  return (
    <div className="dw-field-row">
      {icon
        ? <span className="dw-field-icon">{icon}</span>
        : <span className="dw-field-label">{label}</span>
      }
      <span className="dw-field-sep">|</span>
      <div style={{ flex: 1 }}>{children}</div>
    </div>
  );
}

// ─── QR block ────────────────────────────────────────────────────────────────
function QRBlock({ src }) {
  if (!src) return null;
  const url = getMediaUrl(src);
  return (
    <div className="dw-qr">
      <img className="dw-qr-img" src={url} alt="QR" />
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────
const AutoWalletDeposit = () => {
  const [mainTab,      setMainTab]      = useState("pkr");
  const [pkrSub,       setPkrSub]       = useState(TYPE_BANK);
  const [accountTab,   setAccountTab]   = useState(0);
  const [addresses,    setAddresses]    = useState({});
  const [loading,      setLoading]      = useState(true);

  const [amount,       setAmount]       = useState("");
  const [usdtAmt,      setUsdtAmt]      = useState("");
  const [pkrEq,        setPkrEq]        = useState("");
  const [utr,          setUtr]          = useState("");
  const [imageFile,    setImageFile]    = useState(null);
  const [imageBase64,  setImageBase64]  = useState("");
  const [selectedChip, setSelectedChip] = useState(null);
  const [submitting,   setSubmitting]   = useState(false);
  const [message,      setMessage]      = useState({ text: "", isError: false });
  const [fileInputKey, setFileInputKey] = useState(0);

  const clearImageSelection = () => {
    setImageFile(null);
    setImageBase64("");
    setFileInputKey((k) => k + 1);
  };

  const validateDepositForm = () => {
    const finalAmount = mainTab === "usdt" ? parseFloat(usdtAmt) : parseFloat(amount);
    if (!finalAmount || finalAmount <= 0) {
      return "Please enter a valid amount";
    }

    const utrTrimmed = utr.trim();
    if (!utrTrimmed) {
      return "Transaction Number is required";
    }

    if (!imageBase64) {
      return "Transaction image is required";
    }

    return null;
  };

  const fetchAddresses = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.get("/wallet/referrer-deposit-addresses");
      if (res.data?.status) setAddresses(res.data.data || {});
    } catch (err) {
      console.error("fetch deposit addresses:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAddresses(); }, [fetchAddresses]);

  const handleImageChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onloadend = () => { setImageBase64(reader.result); setImageFile(file); };
    reader.readAsDataURL(file);
  };

  const payMode = mainTab === "usdt" ? TYPE_USDT : pkrSub;
  const rawAddr = addresses[payMode] || addresses[String(payMode)] || null;
  const addrList = Array.isArray(rawAddr) ? rawAddr : rawAddr ? [rawAddr] : [];
  const activeAccount = accountTab < addrList.length ? accountTab : 0;
  const selectedAddr = addrList[activeAccount] || {};
  const addr    = addrList[0] || {};

  const switchMain = (tab) => {
    setMainTab(tab);
    setSelectedChip(null);
    setMessage({ text: "", isError: false });
  };

  const handleSubmit = async () => {
    setMessage({ text: "", isError: false });
    const validationError = validateDepositForm();
    if (validationError) {
      setMessage({ text: validationError, isError: true });
      return;
    }

    const finalAmount = mainTab === "usdt" ? parseFloat(usdtAmt) : parseFloat(amount);
    setSubmitting(true);
    try {
      const res = await axiosInstance.post("/wallet/deposit-request", {
        pay_mode: payMode,
        amount: finalAmount,
        utr_number: utr.trim(),
        image_base64: imageBase64,
      });
      if (res.data?.status) {
        setMessage({ text: res.data.message || "Deposit request submitted!", isError: false });
        setAmount("");
        setUsdtAmt("");
        setPkrEq("");
        setUtr("");
        setSelectedChip(null);
        clearImageSelection();
      } else {
        setMessage({ text: res.data?.message || "Submission failed", isError: true });
      }
    } catch (err) {
      setMessage({ text: err?.response?.data?.message || "Submission failed", isError: true });
    } finally {
      setSubmitting(false);
    }
  };

  const usdtIcon = (
    <img src="/assets/autoWallet/png/usdt-logo.png"
      alt="USDT" style={{ height: 18, verticalAlign: "middle" }} />
  );

  return (
    <ClientLayout showLoader={false}>
      <style>{css}</style>
      <div className="dw-page">
        {/* Title */}
        <div className="dw-page-title">
          Deposit
          <Link to="/auto-wallet/my-deposits" className="dw-title-link">
            View Requests
          </Link>
        </div>

        <div className="dw-container">
          {/* ── Main tabs ── */}
          <div className="dw-tabs">
            <div
              className={`dw-main-tab${mainTab === "pkr" ? " active" : ""}`}
              onClick={() => switchMain("pkr")}
            >
              <div
                className={`dw-main-tab-inner${
                  mainTab === "pkr" ? " dark" : " light"
                }`}
              >
                <img
                  className="dw-main-icon"
                  src="/assets/autoWallet/png/deposit_rs.png"
                  alt="UPI"
                />
                <div
                  className={`dw-main-tab-label${
                    mainTab === "pkr" ? " light-text" : " dark-text"
                  }`}
                >
                  DEPOSIT RS
                </div>
              </div>
            </div>

            <div
              className={`dw-main-tab${mainTab === "usdt" ? " active" : ""}`}
              onClick={() => switchMain("usdt")}
            >
              <div
                className={`dw-main-tab-inner${
                  mainTab === "usdt" ? " dark" : " light"
                }`}
              >
                <img
                  className="dw-main-icon"
                  src="/assets/autoWallet/png/usdt-logo.png"
                  alt="USDT"
                />
                <div
                  className={`dw-main-tab-label${
                    mainTab === "usdt" ? " light-text" : " dark-text"
                  }`}
                >
                  DEPOSIT USDT
                </div>
              </div>
            </div>
          </div>

          {loading ? (
            <div
              style={{ textAlign: "center", padding: "40px", color: "#666" }}
            >
              Loading...
            </div>
          ) : (
            <>
              {/* ══════════ PKR ══════════ */}
              {mainTab === "pkr" && (
                <div className="dw-card">
                  {/* Sub-tabs */}
                  <div className="dw-sub-tabs">
                    {[
                      {
                        id: TYPE_BANK,
                        content: (
                          <img
                            className="dw-pay-logo"
                            src="/assets/autoWallet/png/bank.png"
                            alt="Bank"
                          />
                        )
                      },
                      {
                        id: TYPE_JAZZ,
                        content: (
                          <img
                            className="dw-pay-logo"
                            src="/assets/autoWallet/png/jazzcash-logo.png"
                            alt="JazzCash"
                          />
                        )
                      },
                      {
                        id: TYPE_EASY,
                        content: (
                          <img
                            className="dw-pay-logo"
                            src="/assets/autoWallet/png/easypaisa2.png"
                            alt="EasyPaisa"
                          />
                        )
                      }
                    ].map(({ id, content }) => (
                      <div
                        key={id}
                        className={`dw-sub-tab${
                          pkrSub === id ? " active" : ""
                        }`}
                        onClick={() => {
                          setPkrSub(id);
                          setAccountTab(0);
                          setSelectedChip(null);
                        }}
                      >
                        {content}
                      </div>
                    ))}
                  </div>

                  {addrList.length === 0 ? (
                    <p className="dw-empty">
                      No payment details configured for this method yet.
                    </p>
                  ) : (
                    <>
                      {addrList.length > 1 && (
                        <div className="dw-account-tabs">
                          {addrList.map((item, index) => (
                            <button
                              key={item.deposit_id || index}
                              type="button"
                              className={`dw-account-tab${
                                activeAccount === index ? " active" : ""
                              }`}
                              onClick={() => setAccountTab(index)}
                            >
                              {item.account_title || `Account ${index + 1}`}
                            </button>
                          ))}
                        </div>
                      )}
                      <QRBlock src={selectedAddr.qrcode_path} />
                      {pkrSub === TYPE_BANK && (
                        <>
                          <InfoRow
                            label="Account Title"
                            value={selectedAddr.account_title}
                            placeholder="Account Title"
                          />
                          <InfoRow
                            label="Bank Name"
                            value={selectedAddr.bank_name}
                            placeholder="Bank Name"
                          />
                          <InfoRow
                            label="Account"
                            value={selectedAddr.account_number}
                            placeholder="Account Number"
                            last
                          />
                        </>
                      )}
                      {(pkrSub === TYPE_JAZZ || pkrSub === TYPE_EASY) && (
                        <>
                          <InfoRow
                            label="Account Title"
                            value={selectedAddr.account_title}
                            placeholder="Account Title"
                          />
                          <InfoRow
                            label="Account"
                            value={selectedAddr.account_number}
                            placeholder="Account Number"
                            last
                          />
                        </>
                      )}
                    </>
                  )}

                  <div className="dw-section-title">
                    <img
                      src="/assets/autoWallet/png/saveWallet-26023150.png"
                      alt=""
                      style={{ height: 20 }}
                    />
                    SELECT AMOUNT
                  </div>
                  <div className="dw-chips">
                    {PKR_AMOUNTS.map((v) => (
                      <div
                        key={v}
                        className={`dw-chip${
                          selectedChip === v ? " active" : ""
                        }`}
                        onClick={() => {
                          setSelectedChip(v);
                          setAmount(String(v));
                        }}
                      >
                        <span className="dw-chip-currency">Rs.</span>
                        {v.toLocaleString()}
                      </div>
                    ))}
                  </div>

                  <InputRow label="Amount Rs.">
                    <input
                      className="dw-input"
                      type="number"
                      placeholder="Please Enter Amount"
                      value={amount}
                      onChange={(e) => {
                        setAmount(e.target.value);
                        setSelectedChip(null);
                      }}
                    />
                  </InputRow>
                  <InputRow label="Transaction Number">
                    <input
                      className="dw-input"
                      type="text"
                      placeholder="Transaction Number"
                      value={utr}
                      onChange={(e) => setUtr(e.target.value)}
                    />
                  </InputRow>
                  <InputRow label="Image">
                    <input
                      key={`deposit-image-${fileInputKey}`}
                      className="dw-input"
                      type="file"
                      accept="image/*"
                      onChange={handleImageChange}
                    />
                    {imageFile && (
                      <img
                        src={imageBase64}
                        alt="preview"
                        style={{
                          width: 60,
                          height: 60,
                          objectFit: "cover",
                          borderRadius: 6,
                          marginTop: 6
                        }}
                      />
                    )}
                  </InputRow>
                </div>
              )}

              {/* ══════════ USDT ══════════ */}
              {mainTab === "usdt" && (
                <div className="dw-card">
                  <div className="dw-section-title">
                    {usdtIcon} PAYMENT ADDRESS
                  </div>

                  {addr.bep20_address ? (
                    <InfoRow label="BEP20" value={addr.bep20_address} />
                  ) : (
                    <p className="dw-empty">No USDT address configured yet.</p>
                  )}

                  <QRBlock src={addr.qrcode_path} />

                  <div className="dw-section-title" style={{ marginTop: 18 }}>
                    {usdtIcon} SELECT AMOUNT
                  </div>
                  <div className="dw-chips">
                    {USDT_AMOUNTS.map((v) => (
                      <div
                        key={v}
                        className={`dw-chip${
                          selectedChip === v ? " active" : ""
                        }`}
                        onClick={() => {
                          setSelectedChip(v);
                          setUsdtAmt(String(v));
                          setPkrEq(String(v * USDT_RATE));
                        }}
                      >
                        <img
                          src="/assets/autoWallet/png/usdt-logo.png"
                          alt=""
                          style={{ height: 14, marginRight: 4 }}
                        />
                        {v}
                      </div>
                    ))}
                  </div>

                  {/* USDT amount */}
                  <InputRow icon={usdtIcon}>
                    <input
                      className="dw-input"
                      type="number"
                      placeholder="Please enter USDT amount"
                      value={usdtAmt}
                      onChange={(e) => {
                        const v = e.target.value;
                        setUsdtAmt(v);
                        setPkrEq(v ? String(parseFloat(v) * USDT_RATE) : "");
                        setSelectedChip(null);
                      }}
                    />
                  </InputRow>

                  {/* PKR equivalent */}
                  <InputRow label="Rs.">
                    <input
                      className="dw-input"
                      type="number"
                      placeholder="Please enter the amount"
                      value={pkrEq}
                      onChange={(e) => {
                        const v = e.target.value;
                        setPkrEq(v);
                        setUsdtAmt(
                          v ? (parseFloat(v) / USDT_RATE).toFixed(2) : ""
                        );
                        setSelectedChip(null);
                      }}
                    />
                  </InputRow>

                  <InputRow label="Transaction Number">
                    <input
                      className="dw-input"
                      type="text"
                      placeholder="Transaction Number"
                      value={utr}
                      onChange={(e) => setUtr(e.target.value)}
                    />
                  </InputRow>
                  <InputRow label="Image">
                    <input
                      key={`deposit-image-${fileInputKey}`}
                      className="dw-input"
                      type="file"
                      accept="image/*"
                      onChange={handleImageChange}
                    />
                    {imageFile && (
                      <img
                        src={imageBase64}
                        alt="preview"
                        style={{
                          width: 60,
                          height: 60,
                          objectFit: "cover",
                          borderRadius: 6,
                          marginTop: 6
                        }}
                      />
                    )}
                  </InputRow>
                </div>
              )}

              {message.text && (
                <div
                  className={`dw-msg${message.isError ? " error" : " success"}`}
                >
                  {message.text}
                </div>
              )}

              <button
                className="dw-submit-btn"
                onClick={handleSubmit}
                disabled={submitting}
              >
                {submitting ? "Please wait..." : "Deposit"}
              </button>

              {/* Instructions */}
              <div className="dw-instructions">
                <div className="dw-section-title">
                  <img
                    src="/assets/autoWallet/png/balance.png"
                    alt=""
                    style={{ height: 20 }}
                  />
                  Recharge Instructions
                </div>
                <ul
                  style={{
                    paddingLeft: 18,
                    color: "#666",
                    fontSize: 13,
                    lineHeight: 1.8,
                    margin: 0
                  }}
                >
                  <li>
                    If the transfer time is up, please fill out the deposit form
                    again.
                  </li>
                  <li>
                    The transfer amount must match the order you created,
                    otherwise money cannot be credited.
                  </li>
                  <li>
                    If you transfer the wrong amount, our company will not be
                    responsible for the lost amount.
                  </li>
                  <li>
                    Do not cancel the deposit order after the money has been
                    transferred.
                  </li>
                </ul>
              </div>
            </>
          )}
        </div>
      </div>
    </ClientLayout>
  );
};

// ─── CSS ──────────────────────────────────────────────────────────────────────
const css = `
  .dw-page {
    background: #eef0f3;
    min-height: 100vh;
    padding-bottom: 50px;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  }

  /* Title bar */
  .dw-page-title {
    position: relative;
    text-align: center;
    background: var(--theme-color);
    color: #fff;
    font-size: 17px;
    font-weight: 600;
    padding: 12px 16px;
    letter-spacing: 0.5px;
  }
  .dw-title-link {
    position: absolute;
    right: 14px;
    top: 50%;
    transform: translateY(-50%);
    font-size: 12px;
    font-weight: 600;
    color: #f0c060;
    text-decoration: none;
    background: rgba(255,255,255,0.12);
    border: 1px solid rgba(255,255,255,0.25);
    border-radius: 20px;
    padding: 4px 10px;
    white-space: nowrap;
    letter-spacing: 0.3px;
    transition: background 0.15s;
  }
  .dw-title-link:hover { background: rgba(255,255,255,0.22); color: #ffe9a0; text-decoration: none; }

  .dw-container { padding: 14px 12px; }

  /* ── Main tabs ── */
  .dw-tabs { display: flex; gap: 8px; margin-bottom: 14px; }
  .dw-main-tab { flex: 1; cursor: pointer; border-radius: 10px; overflow: hidden; }
  .dw-main-tab-inner {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 12px 8px;
    min-height: 100px;
  }
  .dw-main-tab-inner.light {
    background: #fff;
    border: 1px solid #ddd;
    border-radius: 10px;
  }
  .dw-main-tab-inner.dark {
    background: linear-gradient(160deg, var(--theme-color) 0%, #3d6b8b 100%);
    border-radius: 10px;
  }
  .dw-main-tab-label {
    font-size: 12px;
    font-weight: 700;
    margin-top: 6px;
    text-align: center;
    letter-spacing: 0.3px;
  }
  .dw-main-tab-label.dark-text  { color: #333; }
  .dw-main-tab-label.light-text { color: #fff; }
  .dw-main-icon { height: 40px; object-fit: contain; }

  /* ── White card ── */
  .dw-card {
    background: #fff;
    border-radius: 14px;
    box-shadow: 0 2px 12px rgba(0,0,0,0.08);
    padding: 16px;
    margin-bottom: 14px;
  }

  /* ── Section title ── */
  .dw-section-title {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 14px;
    font-weight: 700;
    color: #222;
    margin-bottom: 12px;
    text-transform: uppercase;
  }

  /* ── Sub-tabs (Bank / Jazz / Easy) ── */
  .dw-sub-tabs {
    display: flex;
    gap: 6px;
    margin-bottom: 14px;
  }
  .dw-sub-tab {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0;
    background: #f5f5f5;
    border: 1.5px solid #e0e0e0;
    border-radius: 8px;
    cursor: pointer;
    height: 100px;
    overflow: hidden;
    transition: border-color 0.15s, background 0.15s;
  }
  .dw-sub-tab.active {
    background: linear-gradient(160deg, var(--theme-color) 0%, #3d6b8b 100%);
    border-color: var(--theme-color);
  }
  .dw-sub-tab.active .dw-sub-text { color: #fff; }
  .dw-sub-text { font-size: 13px; font-weight: 700; color: #333; padding: 8px 4px; }
  .dw-pay-logo { width: 90%; height: 100px; object-fit: contain; }

  .dw-qr { text-align: center; margin: 16px 0 12px; }
  .dw-qr-img {
    width: 180px;
    height: 180px;
    border-radius: 10px;
    object-fit: contain;
  }

  @media (max-width: 767px) {
    .dw-main-tab-inner { min-height: 68px; padding: 8px 6px; }
    .dw-main-icon { height: 26px; }
    .dw-main-tab-label { margin-top: 4px; font-size: 11px; }
    .dw-sub-tab { height: 62px; }
    .dw-pay-logo { height: 52px; }
    .dw-qr { margin: 8px 0; }
    .dw-qr-img { width: 120px; height: 120px; }
  }

  .dw-account-tabs {
    display: flex;
    gap: 8px;
    margin-bottom: 14px;
    overflow-x: auto;
  }
  .dw-account-tab {
    flex: 0 0 auto;
    border: 1.5px solid #e0e0e0;
    background: #f5f5f5;
    color: #333;
    border-radius: 8px;
    padding: 8px 14px;
    font-size: 13px;
    font-weight: 700;
    cursor: pointer;
    white-space: nowrap;
  }
  .dw-account-tab.active {
    background: linear-gradient(160deg, var(--theme-color) 0%, #3d6b8b 100%);
    border-color: var(--theme-color);
    color: #fff;
  }

  /* ── Field rows (info + input) ── */
  .dw-field-row {
    display: flex;
    align-items: center;
    background: #f5f7f9;
    border: 1px solid #e8eaed;
    border-radius: 8px;
    padding: 10px 12px;
    margin-bottom: 10px;
    min-height: 44px;
  }
  .dw-field-label {
    font-size: 12px;
    font-weight: 600;
    color: #000000;
    white-space: nowrap;
    min-width: 70px;
    flex-shrink: 0;
  }
  .dw-field-icon {
    display: flex;
    align-items: center;
    min-width: 36px;
    flex-shrink: 0;
  }
  .dw-field-sep {
    color: #ccc;
    margin: 0 10px;
    font-size: 18px;
    flex-shrink: 0;
    line-height: 1;
  }
  .dw-field-value {
    flex: 1;
    font-size: 13px;
    color: #555;
    word-break: break-all;
  }
  .dw-input {
    width: 100%;
    border: none;
    outline: none;
    background: transparent;
    font-size: 13px;
    color: #333;
  }
  .dw-input::placeholder { color: #aaa; }
  .dw-field-readonly { flex: 1; min-width: 0; }
  .dw-field-readonly.has-value {
    color: #000000;
    font-weight: 700;
  }

  /* ── Amount chips ── */
  .dw-chips {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 8px;
    margin-bottom: 14px;
  }
  .dw-chip {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
    padding: 10px 4px;
    background: #f0f2f5;
    border: 1px solid #e0e0e0;
    border-radius: 8px;
    font-size: 14px;
    font-weight: 600;
    color: #000000;
    cursor: pointer;
    transition: background 0.15s, border-color 0.15s;
  }
  .dw-chip.active {
    background: #494949;
    border-color: #494949;
    color: #fff;
  }
  .dw-chip:hover:not(.active) { background: #e5e8ec; }
  .dw-chip-currency { font-size: 11px; color: #000000; }
  .dw-chip.active .dw-chip-currency { color: #fff; }

  /* ── Empty state ── */
  .dw-empty {
    text-align: center;
    padding: 16px 0;
    color: #999;
    font-size: 13px;
    margin: 0 0 8px;
  }

  /* ── Message ── */
  .dw-msg {
    text-align: center;
    padding: 10px 14px;
    border-radius: 8px;
    margin-bottom: 12px;
    font-size: 14px;
    font-weight: 500;
  }
  .dw-msg.success { background: #e8f9f0; color: #1a8a55; border: 1px solid #b2e8cc; }
  .dw-msg.error   { background: #fef0f0; color: #c0392b; border: 1px solid #f5c0bb; }

  /* ── Submit button ── */
  .dw-submit-btn {
    width: 100%;
    padding: 15px;
    background: #02b181;
    color: #fff;
    font-size: 16px;
    font-weight: 700;
    border: none;
    border-radius: 50px;
    cursor: pointer;
    margin-bottom: 18px;
    letter-spacing: 0.5px;
    transition: opacity 0.2s;
    box-shadow: 0 4px 15px rgba(2,177,129,0.35);
  }
  .dw-submit-btn:disabled { opacity: 0.6; cursor: not-allowed; }
  .dw-submit-btn:hover:not(:disabled) { opacity: 0.88; }

  /* ── Instructions ── */
  .dw-instructions {
    background: #fff;
    border-radius: 14px;
    box-shadow: 0 2px 12px rgba(0,0,0,0.06);
    padding: 16px;
  }
`;

export default AutoWalletDeposit;
